import type { ReportRow } from '../api/types.js';

export interface CycleLeg {
  label: string;
  from: string;
  to: string;
  average: number | null;
  median: number | null;
  /** Work orders this leg could be measured over, i.e. that reached its start. */
  eligible: number;
  /** Work orders it actually was measured over. */
  count: number;
  /** Work orders whose recorded dates run backwards, so they are not averaged. */
  excluded: number;
}

export interface ReportSummary {
  legs: CycleLeg[];
  approvedTotalCents: number;
}

function average(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2
    : (sorted[middle] as number);
}

/**
 * A duration cannot run backwards. Where a recorded one does, the dates behind it are
 * wrong, and averaging it in reports a negative cycle time as though it were a
 * measurement, so it is set aside and counted for the reader to see.
 */
function leg(from: string, to: string, values: readonly number[], eligible: number): CycleLeg {
  const usable = values.filter((value) => value >= 0);
  return {
    label: `${from} to ${to}`,
    from,
    to,
    average: average(usable),
    median: median(usable),
    eligible,
    count: usable.length,
    excluded: values.length - usable.length,
  };
}

/**
 * Cycle time is two distinct measurements — how long approval took, and how long the
 * work took once approved — so each is reported as a leg carrying its own denominator
 * rather than as a bare average the reader has to interpret.
 */
export function summarize(rows: readonly ReportRow[]): ReportSummary {
  const numbers = (key: string) =>
    rows
      .map((row) => row[key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  const approvals = numbers('daysToApproval');

  return {
    legs: [
      leg('Request', 'Approval', approvals, rows.length),
      // Only an approved work order can be completed.
      leg('Approval', 'Completion', numbers('daysToCompletion'), approvals.length),
    ],
    // Cents are integers, so this sum is exact.
    approvedTotalCents: numbers('projectCostCents').reduce((sum, value) => sum + value, 0),
  };
}
